import React, { useState, useEffect, useRef } from 'react';
import sanitizeHtml from 'sanitize-html';
import { useHistory } from 'react-router-dom';
import {
  TextField,
  FormControl,
  InputAdornment,
  IconButton,
  FormHelperText,
  Box,
  Checkbox,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { MdMailOutline, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { DSButton, DSLink, DSText } from '@engenharia/ds-react';
import Cookies from 'js-cookie';
import decode from 'jwt-decode';
import { BiLeftArrowAlt } from 'react-icons/bi';

import api from '../../services/api';
import authApi from '../../services/authApi';
import { redirectUser } from '../../services/user';

import { getUrlParam } from '../../utils/params';
import Tip from '../../components/Tip';

import {
  BackBtn,
  Container,
  Card,
  ChoiceGroup,
  EmailPasswordChoiceBtn,
  EmailPasswordBtnContent,
  EmailPasswordBtnPrefix,
  EmailPasswordBtnHighlight,
  EmailPasswordBtnIcon,
  MagicLinkChoiceBtn,
  MagicLinkTitle,
  SignupBox,
  LinkButton,
  UnconfirmedAccountPrefix,
  UnconfirmedAccountHighlight,
  SignInFreeButton,
  Image,
  Section,
  CallToActionBox,
  ContainerBanner,
  CheckboxContainer,
  SuccessBox,
  SuccessIcon,
  SuccessTitle,
  SuccessSubtitle,
  ResendRow,
  ResendButton,
  OtherWaysButton,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
} from './styles';

import Button from '../../components/atoms/Button';
import Logo from '../../components/molecules/Logo';
import Notification from '../../components/Notification';
import MetaHeader from '../../components/MetaHeader';
import {
  mailValidation,
  passwordValidation,
  strongPasswordValidation,
} from '../../utils/validations';
import { getLanguage, setAccountType, setOtpToken } from '../../services/auth';
import { SocialIconButtons } from '../../components/molecules/SocialIconButton';
import { GoogleSignInButton } from '../../components/molecules/GoogleSignInButton';
import { Hidden } from '../../components/utilities/Hidden';
import Header from '../../components/Header';
import BlackFriday from '../../components/Banner/BlackFriday';
import { transcodeLanguage } from '../../utils/language';
import { getDomain } from '../../utils/domain';
import Captcha from '../../components/Captcha';
import CircularLoader from '../../components/CircularLoader';
import { isReactNativeWebView } from '../../utils/isReactNativeWebView';
import {
  clearRecentAccounts,
  getRecentAccounts,
  saveRecentAccount,
} from '../../utils/recentAccounts';
import { redirectAppleLogin, redirectMicrosoftLogin } from '../../utils/providerLogin';
import { getMarketingParams, persistMarketingParams } from '../../utils/marketingParams';
import { getLoginCallbackUrl, persistMcpOAuthParams } from '../../utils/mcpOAuthParams';
import { RecentAccountsList } from '../../components/molecules/RecentAccountsList';

const handleNameCookieWeakPassword = (accountId) => `mlabs-weak-password-${accountId}`;

export default function SignIn() {
  const { t } = useTranslation();
  const history = useHistory();
  const captchaRef = useRef(null);

  const [state, setState] = useState({
    email: '',
    emailValidation: {},
    password: '',
    passwordValidation: {},
    showPassword: false,
    persistent: true,
  });
  const [notification, setNotification] = useState({});
  const [providerNotification, setproviderNotification] = useState({});
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [activeMode, setActiveMode] = useState(null); // null | 'password' | 'magic'
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const [loadingMagicLink, setLoadingMagicLink] = useState(false);
  const [showOtherWaysModal, setShowOtherWaysModal] = useState(false);
  const [recentAccounts, setRecentAccounts] = useState(() => getRecentAccounts());
  const [showAllLoginOptions, setShowAllLoginOptions] = useState(
    () => getRecentAccounts().length === 0,
  );

  const resetNestedLoginViews = () => {
    setActiveMode(null);
    setMagicLinkSent(false);
    setShowOtherWaysModal(false);
  };

  const openAllLoginOptions = () => {
    if (!showAllLoginOptions) {
      window.history.pushState({ signInView: 'all' }, '');
    }
    setShowAllLoginOptions(true);
  };

  const persistLoginAccount = ({ name, email, provider }) => {
    if (!email || !provider) return;
    setRecentAccounts(saveRecentAccount({ name, email, provider }));
  };

  const extractAccountFromToken = (token) => {
    try {
      const payload = decode(token);
      return {
        name: payload.name || payload.full_name || payload.email?.split('@')[0],
        email: payload.email || payload.username,
      };
    } catch {
      return {};
    }
  };

  useEffect(() => {
    persistMarketingParams();
    persistMcpOAuthParams();
  }, []);

  useEffect(() => {
    if (window.ReactNativeWebView && getUrlParam('access_token')) {
      const message = {
        status: 200,
        data: {
          logged: false,
        },
      };

      window.ReactNativeWebView.postMessage(JSON.stringify(message));
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    if (error === 'apple_login_failed' || error === 'apple_missing_token') {
      setproviderNotification({
        key: new Date().getTime(),
        message: t('errors.apple-login-failed'),
      });
    }
  }, []);

  useEffect(() => {
    window.history.replaceState({ signInView: 'main' }, '');

    const onPopState = (event) => {
      const view = event.state?.signInView;
      resetNestedLoginViews();

      if (view === 'all') {
        setShowAllLoginOptions(true);
        return;
      }

      if (view === 'main') {
        setShowAllLoginOptions(getRecentAccounts().length === 0);
        return;
      }

      // Block returning to the previous authenticated app page after logout.
      setShowAllLoginOptions(getRecentAccounts().length === 0);
      window.history.pushState({ signInView: 'main' }, '');
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const handlePersistentChange = () => {
    setState((currentState) => ({
      ...currentState,
      persistent: !currentState.persistent,
    }));
  };

  const handleEmailChange = (event) => {
    setState({
      ...state,
      email: event.target.value,
      emailValidation: mailValidation(event.target.value),
    });
  };

  const handlePasswordChange = (event) => {
    setState({
      ...state,
      password: event.target.value,
      passwordValidation: passwordValidation(event.target.value),
    });
  };

  const handleClickShowPassword = () => {
    setState({ ...state, showPassword: !state.showPassword });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const startResendCountdown = () => {
    setResendSeconds(60);
    const interval = setInterval(() => {
      setResendSeconds((s) => {
        if (s <= 1) {
          clearInterval(interval);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const handleMagicLinkSubmit = async () => {
    const { email } = state;
    if (!email || mailValidation(email).emailInvalidError) {
      setNotification({ key: new Date().getTime(), message: t('errors.email-invalid') });
      return;
    }
    setLoadingMagicLink(true);
    try {
      const payload = {
        email,
        language: transcodeLanguage(getLanguage()),
      };
      // When inside the mobile app WebView, flag the request so the magic link
      // email redirects back to the app via deep link.
      if (window.ReactNativeWebView) {
        payload.mobile_device = true;
      }
      await authApi.post('/magic_link', payload);
      setMagicLinkSent(true);
      startResendCountdown();
    } catch (error) {
      const { response } = error;
      setNotification({
        key: new Date().getTime(),
        message: response?.data?.message || t('errors.connection-failure'),
      });
    } finally {
      setLoadingMagicLink(false);
    }
  };

  const handleResend = async () => {
    if (resendSeconds > 0) return;
    setMagicLinkSent(false);
    await handleMagicLinkSubmit();
  };

  const clickActionButton = async (link) => {
    try {
      const url = new URL(link);
      url.searchParams.set('email', state.email);
      const response = await api.get(url.toString());

      setNotification({
        key: new Date().getTime(),
        message: response?.data?.message || t('errors.connection-failure'),
      });
    } catch (error) {
      setNotification({
        key: new Date().getTime(),
        message: t('errors.connection-failure'),
      });
    }
  };

  const notificationButton = (options) => (
    <DSLink
      href=""
      onClick={() => clickActionButton(options.link)}
      variant="button"
      color="secondary"
    >
      {options.message}
    </DSLink>
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loadingSubmit) {
      return;
    }
    const { email, password, persistent } = state;

    setLoadingSubmit(true);

    try {
      if (!email || !password) {
        setLoadingSubmit(false);
        setNotification({ key: new Date().getTime(), message: t('errors.login-invalid') });
        return;
      }

      await captchaRef?.current?.reset();
      const captcha = await captchaRef?.current?.executeAsync();

      const response = await authApi.post('/accounts/sign_in', {
        email,
        password,
        callback_url: getLoginCallbackUrl(),
        language: transcodeLanguage(getLanguage()),
        captcha,
        persistent,
        ...(window.ReactNativeWebView ? { mobile_device: true } : {}),
      });

      const accountId =
        decode(response?.data?.token)?.account_id || decode(response?.data?.token)?.username;
      const otpName = decode(response?.data?.token)?.otp_name;

      if (
        !Cookies.get(handleNameCookieWeakPassword(accountId)) &&
        strongPasswordValidation(password).passwordInvalidError
      ) {
        Cookies.set(handleNameCookieWeakPassword(accountId), false, {
          expires: 30,
          domain: getDomain(),
        });
      }

      if (otpName === undefined || otpName === null) {
        persistLoginAccount({ name: email.split('@')[0], email, provider: 'email' });
        await redirectUser(response);
      } else if (response.data?.token && response.data?.token !== '') {
        setOtpToken(response.data.token);
        history.push('/accounts/verify');
      } else {
        history.push('/accounts/sign_in');
      }
    } catch (error) {
      const { response } = error;

      const actionButton = response?.data?.options && notificationButton(response.data.options);

      setNotification({
        key: new Date().getTime(),
        message: response?.data?.message || t('errors.connection-failure'),
        button: actionButton,
      });

      setLoadingSubmit(false);

      if (response?.data?.options) {
        setState({
          ...state,
          emailValidation: { emailUnconfirmedError: true },
        });
      } else {
        setState({
          ...state,
          emailValidation: { emailInvalidError: true },
          passwordValidation: { passwordInvalidError: true },
        });
      }
    }
  };

  const handleSignUpRedirect = async (event) => {
    event.preventDefault();
    history.push('/start');
  };

  const handleFacebookLogin = async ({ profile, tokenDetail }) => {
    if (!tokenDetail?.accessToken || !profile?.email) {
      return;
    }

    const facebookUserId = tokenDetail?.userID ?? (profile?.id != null ? String(profile.id) : '');
    try {
      const response = await authApi.post('/provider_sign_in', {
        provider: 'facebook',
        user_id: facebookUserId,
        token: tokenDetail.accessToken,
        name: profile.name,
        email: profile.email,
        captcha: process.env.REACT_APP_CAPTCHA_UUID,
        callback_url: getLoginCallbackUrl(),
        language: transcodeLanguage(getLanguage()),
        ...getMarketingParams(),
        ...(window.ReactNativeWebView ? { mobile_device: true } : {}),
      });

      setAccountType('facebook');
      persistLoginAccount({
        name: profile.name,
        email: profile.email,
        provider: 'facebook',
      });
      await redirectUser(response);
    } catch (error) {
      const { response } = error;

      const actionButton = response?.data?.options && notificationButton(response.data.options);

      setproviderNotification({
        key: new Date().getTime(),
        message: response?.data?.message || t('errors.connection-failure'),
        button: actionButton,
      });
    }
  };

  const handleMicrosoftLogin = async (data) => {
    if (data?.error || !data?.code) {
      setproviderNotification({
        key: new Date().getTime(),
        message: data?.error || t('errors.connection-failure'),
      });
      return;
    }
    try {
      const response = await authApi.post('/provider_sign_in', {
        provider: 'microsoft',
        user_id: 'microsoft_code_exchange',
        token: data.code,
        redirect_uri: data.redirectUri,
        captcha: process.env.REACT_APP_CAPTCHA_UUID,
        callback_url: getLoginCallbackUrl(),
        language: transcodeLanguage(getLanguage()),
        persistent: state.persistent,
        ...getMarketingParams(),
        ...(window.ReactNativeWebView ? { mobile_device: true } : {}),
      });
      setAccountType('microsoft');
      persistLoginAccount({
        name: response?.data?.name || extractAccountFromToken(response?.data?.token)?.name,
        email: response?.data?.email || extractAccountFromToken(response?.data?.token)?.email,
        provider: 'microsoft',
      });
      await redirectUser(response);
    } catch (error) {
      const { response } = error;
      setproviderNotification({
        key: new Date().getTime(),
        message: response?.data?.message || t('errors.connection-failure'),
      });
    }
  };

  const handleAppleLogin = async (data) => {
    if (data?.error || !data?.token || !data?.user) {
      setproviderNotification({
        key: new Date().getTime(),
        message: data?.error || t('errors.connection-failure'),
      });
      return;
    }
    try {
      const response = await authApi.post('/provider_sign_in', {
        provider: 'apple',
        user_id: data.user.sub,
        token: data.token,
        name: data.user.name,
        email: data.user.email,
        captcha: process.env.REACT_APP_CAPTCHA_UUID,
        callback_url: getLoginCallbackUrl(),
        language: transcodeLanguage(getLanguage()),
        persistent: state.persistent,
        ...getMarketingParams(),
        ...(window.ReactNativeWebView ? { mobile_device: true } : {}),
      });
      setAccountType('apple');
      persistLoginAccount({
        name: data.user.name,
        email: data.user.email,
        provider: 'apple',
      });
      await redirectUser(response);
    } catch (error) {
      const { response } = error;
      setproviderNotification({
        key: new Date().getTime(),
        message: response?.data?.message || t('errors.connection-failure'),
      });
    }
  };

  function sanitize(message) {
    return sanitizeHtml(message, {
      allowedTags: ['strong', 'a', 'u'],
      allowedAttributes: { a: ['href'] },
    });
  }

  const handleRecentAccountSelect = (account) => {
    const { email, provider } = account;

    if (provider === 'email') {
      setState((current) => ({ ...current, email }));
      setActiveMode('password');
      return;
    }

    if (provider === 'magic_link') {
      setState((current) => ({ ...current, email }));
      setActiveMode('magic');
    }
  };

  const handleMicrosoftAccountLogin = (email) => {
    redirectMicrosoftLogin(email);
  };

  const handleAppleAccountLogin = () => {
    redirectAppleLogin();
  };

  const handleRemoveRecentAccounts = () => {
    setRecentAccounts(clearRecentAccounts());
    openAllLoginOptions();
  };

  const hasRecentAccounts = recentAccounts.length > 0;
  const showRecentAccountsView = !activeMode && hasRecentAccounts && !showAllLoginOptions;
  const showDefaultLoginOptions = !activeMode && (!hasRecentAccounts || showAllLoginOptions);

  const signUpBox = (
    <SignupBox>
      <DSText variant="subtitle2" color="textSecondary">
        {t('signin.no-account')}
      </DSText>
      <SignInFreeButton
        data-testid="sign-up-navigation-link"
        onClick={handleSignUpRedirect}
        variant="outline"
        color="orange"
      >
        {t('signin.button.signup-free')}
      </SignInFreeButton>
    </SignupBox>
  );

  const logo = <Logo fullWidth imgComponent={Image} />;

  const inWebView = isReactNativeWebView();

  return (
    <>
      {!inWebView && <Header positionFixed />}
      <Container>
        {!inWebView && (
          <ContainerBanner>
            <BlackFriday />
          </ContainerBanner>
        )}
        <MetaHeader
          title="Login - mLabs | Gestão de redes sociais"
          description="mLabs - Gestão de Redes Sociais | Entrar"
        />
        <Section>
          <CallToActionBox device="laptop">
            <Box marginBottom={16}>{logo}</Box>
            {signUpBox}
          </CallToActionBox>
          <Card>
            <Hidden invert marginBottom={4} device="laptop">
              {logo}
            </Hidden>
            {!activeMode && (
              <>
                {showRecentAccountsView && (
                  <RecentAccountsList
                    accounts={recentAccounts}
                    onSelectAccount={handleRecentAccountSelect}
                    onContinueOther={openAllLoginOptions}
                    onRemoveAccounts={handleRemoveRecentAccounts}
                    onFacebookCompleted={handleFacebookLogin}
                    onMicrosoftLogin={handleMicrosoftAccountLogin}
                    onAppleLogin={handleAppleAccountLogin}
                  />
                )}

                {showDefaultLoginOptions && (
                  <>
                    <ChoiceGroup>
                      <DSText
                        component="h1"
                        variant="h4"
                        style={{
                          marginBottom: '10px',
                          whiteSpace: 'pre-line',
                          color: '#1f1f1f',
                          fontSize: '20px',
                          fontWeight: '500',
                        }}
                      >
                        {t('signin.subtitle')}
                      </DSText>
                      <MagicLinkChoiceBtn type="button" onClick={() => setActiveMode('magic')}>
                        {t('signup.magicLink.button')}
                      </MagicLinkChoiceBtn>
                      <GoogleSignInButton />
                      <SocialIconButtons
                        providers={['facebook']}
                        onFacebookCompleted={handleFacebookLogin}
                        onMicrosoftCompleted={handleMicrosoftLogin}
                        onAppleCompleted={handleAppleLogin}
                      />
                      <EmailPasswordChoiceBtn
                        type="button"
                        onClick={() => {
                          setActiveMode('password');
                        }}
                      >
                        <EmailPasswordBtnContent>
                          <EmailPasswordBtnPrefix>
                            {t('signin.button.email-password')}
                          </EmailPasswordBtnPrefix>
                          <EmailPasswordBtnHighlight>
                            {t('signin.button.email-password-highlight')}
                          </EmailPasswordBtnHighlight>
                        </EmailPasswordBtnContent>
                        <EmailPasswordBtnIcon>
                          <MdMailOutline />
                        </EmailPasswordBtnIcon>
                      </EmailPasswordChoiceBtn>
                    </ChoiceGroup>
                    <OtherWaysButton type="button" onClick={() => setShowOtherWaysModal(true)}>
                      {t('signin.other-ways')}
                    </OtherWaysButton>
                  </>
                )}
              </>
            )}

            {showOtherWaysModal && (
              <ModalOverlay
                onClick={(e) => {
                  if (e.target === e.currentTarget) setShowOtherWaysModal(false);
                }}
              >
                <ModalContent>
                  <ModalHeader>
                    <ModalTitle>{t('signin.other-ways-title')}</ModalTitle>
                    <ModalCloseButton
                      type="button"
                      aria-label="close"
                      onClick={() => setShowOtherWaysModal(false)}
                    >
                      ×
                    </ModalCloseButton>
                  </ModalHeader>
                  <ChoiceGroup>
                    <MagicLinkChoiceBtn
                      type="button"
                      onClick={() => {
                        setShowOtherWaysModal(false);
                        setActiveMode('magic');
                      }}
                    >
                      {t('signup.magicLink.button')}
                    </MagicLinkChoiceBtn>
                    <SocialIconButtons
                      providers={['google']}
                      onFacebookCompleted={handleFacebookLogin}
                      onMicrosoftCompleted={handleMicrosoftLogin}
                      onAppleCompleted={handleAppleLogin}
                    />
                    <EmailPasswordChoiceBtn
                      type="button"
                      onClick={() => {
                        setShowOtherWaysModal(false);
                        setActiveMode('password');
                      }}
                    >
                      <EmailPasswordBtnContent>
                        <EmailPasswordBtnPrefix>
                          {t('signin.button.email-password')}
                        </EmailPasswordBtnPrefix>
                        <EmailPasswordBtnHighlight>
                          {t('signin.button.email-password-highlight')}
                        </EmailPasswordBtnHighlight>
                      </EmailPasswordBtnContent>
                      <EmailPasswordBtnIcon>
                        <MdMailOutline />
                      </EmailPasswordBtnIcon>
                    </EmailPasswordChoiceBtn>
                    <SocialIconButtons
                      providers={['facebook', 'apple', 'microsoft']}
                      onFacebookCompleted={handleFacebookLogin}
                      onMicrosoftCompleted={handleMicrosoftLogin}
                      onAppleCompleted={handleAppleLogin}
                    />
                  </ChoiceGroup>
                </ModalContent>
              </ModalOverlay>
            )}

            {providerNotification?.key && (
              <DSText
                component="p"
                variant="body2"
                color="warning"
                id="provider-error"
                data-testid="providerNotification"
                dangerouslySetInnerHTML={{ __html: sanitize(providerNotification.message) }}
                style={{ backgroundColor: '#ffd04d', padding: '10px' }}
              />
            )}

            {/* ── Magic link form ── */}
            {activeMode === 'magic' && !magicLinkSent && (
              <>
                <FormControl variant="outlined" fullWidth>
                  <BackBtn type="button" onClick={() => setActiveMode(null)}>
                    <BiLeftArrowAlt />
                    {t('button.back')}
                  </BackBtn>
                  <MagicLinkTitle component="h2">{t('signup.magicLink.title')}</MagicLinkTitle>
                  <TextField
                    type="email"
                    id="email"
                    name="email"
                    data-testid="formEmail"
                    label={t('signup.form.email')}
                    value={state.email}
                    onChange={handleEmailChange}
                    variant="outlined"
                    autoComplete="email"
                    margin="normal"
                    autoFocus
                    error={state.emailValidation?.emailInvalidError}
                  />
                  {state.emailValidation?.emailInvalidError && (
                    <FormHelperText
                      error
                      id="email-error"
                      data-testid="email-invalid-error-message"
                    >
                      {t('errors.email-invalid')}
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControl fullWidth>
                  <Button
                    type="button"
                    id="btnMagicLink"
                    onClick={handleMagicLinkSubmit}
                    disabled={loadingMagicLink}
                    endIcon={loadingMagicLink && <CircularLoader color="black" scale="0.4" />}
                  >
                    {!loadingMagicLink && t('signup.magicLink.submit')}
                  </Button>
                </FormControl>
              </>
            )}

            {/* ── Magic link success ── */}
            {activeMode === 'magic' && magicLinkSent && (
              <>
                <BackBtn
                  type="button"
                  onClick={() => {
                    setActiveMode(null);
                    setMagicLinkSent(false);
                  }}
                >
                  <BiLeftArrowAlt />
                  {t('button.back')}
                </BackBtn>
                <SuccessBox>
                  <SuccessIcon>✉️</SuccessIcon>
                  <SuccessTitle>{t('signup.magicLink.success.title')}</SuccessTitle>
                  <SuccessSubtitle>{t('signup.magicLink.success.subtitle')}</SuccessSubtitle>
                  <ResendRow>
                    {resendSeconds > 0 ? (
                      <DSText variant="body3" color="textSecondary">
                        {t('signup.magicLink.success.countdown', { seconds: resendSeconds })}
                      </DSText>
                    ) : (
                      <ResendButton variant="text" onClick={handleResend}>
                        {t('signup.magicLink.success.resend')}
                      </ResendButton>
                    )}
                  </ResendRow>
                </SuccessBox>
              </>
            )}

            {/* ── Password form ── */}
            {activeMode === 'password' && (
              <form onSubmit={handleSubmit} data-testid="form">
                <BackBtn type="button" onClick={() => setActiveMode(null)}>
                  <BiLeftArrowAlt />
                  {t('button.back')}
                </BackBtn>
                <FormControl variant="outlined" fullWidth>
                  <TextField
                    type="email"
                    id="email"
                    name="email"
                    data-testid="formEmail"
                    label={t('signup.form.email')}
                    value={state.email}
                    onChange={handleEmailChange}
                    variant="outlined"
                    autoComplete="email"
                    margin="normal"
                    autoFocus
                    error={
                      state.emailValidation?.emailInvalidError ||
                      state.emailValidation?.emailUnconfirmedError
                    }
                  />
                  {state.emailValidation?.emailInvalidError && (
                    <FormHelperText
                      error
                      id="email-error"
                      data-testid="email-invalid-error-message"
                    >
                      {t('errors.email-invalid')}
                    </FormHelperText>
                  )}
                  {state.emailValidation?.emailUnconfirmedError && (
                    <FormHelperText
                      error
                      id="email-error"
                      data-testid="email-unconfirmed-error-message"
                    >
                      {t('errors.email-unconfirmed')}
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControl variant="outlined" fullWidth>
                  <TextField
                    label={`${t('signup.form.password')}`}
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment>
                          <IconButton
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {state.showPassword ? <MdVisibility /> : <MdVisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    id="password"
                    name="password"
                    data-testid="formPassword"
                    type={state.showPassword ? 'text' : 'password'}
                    value={state.password}
                    onChange={handlePasswordChange}
                    error={state.passwordValidation?.passwordInvalidError}
                  />
                  {state.passwordValidation?.passwordInvalidError && (
                    <FormHelperText
                      error
                      id="password-error"
                      data-testid="password-invalid-error-message"
                    >
                      {t('errors.password-invalid')}
                    </FormHelperText>
                  )}
                </FormControl>
                <CheckboxContainer>
                  <Checkbox
                    color="primary"
                    size="medium"
                    margin="none"
                    checked={state.persistent}
                    onChange={handlePersistentChange}
                  />
                  <DSText variant="caption" weight={400}>
                    {t('signup.form.persistent')}
                  </DSText>
                  <Tip informations={`${t('signup.form.persistent_tip')}`} />
                </CheckboxContainer>
                <FormControl fullWidth>
                  <Button
                    type="submit"
                    id="btnSubmit"
                    endIcon={loadingSubmit && <CircularLoader color="black" scale="0.4" />}
                    disabled={loadingSubmit}
                  >
                    {!loadingSubmit && t('signin.button.signin')}
                  </Button>
                </FormControl>
              </form>
            )}
            <Hidden invert device="laptop">
              {signUpBox}
            </Hidden>
            <Hidden py={2} device="laptop" />
            {activeMode === 'password' && (
              <>
                <LinkButton href="/accounts/forgot_password" variant="button" color="secondary">
                  {t('signin.forgot-password')}
                </LinkButton>
              </>
            )}
            {(activeMode === 'password' || activeMode === null) && (
              <>
                <Box py={1} />
                <LinkButton href="/accounts/confirmation/new" variant="button" color="secondary">
                  <UnconfirmedAccountPrefix>
                    {t('signin.unconfirmed-account-prefix')}
                  </UnconfirmedAccountPrefix>{' '}
                  <UnconfirmedAccountHighlight>
                    {t('signin.unconfirmed-account-link')}
                  </UnconfirmedAccountHighlight>
                </LinkButton>
              </>
            )}
          </Card>
        </Section>
      </Container>
      {notification?.key && (
        <Notification
          index={notification.key}
          message={notification.message}
          button={notification.button}
        />
      )}
      <Captcha ref={captchaRef} />
    </>
  );
}
